import * as React from 'react';
import moment from 'moment-timezone';

import { BillingProvider, Payer } from '../../redux/claim/types';
import {
  Checkbox,
  CommandBarButton,
  DatePicker,
  DetailsList,
  Dropdown,
  IColumn,
  IDropdownOption,
  IObjectWithKey,
  IconButton,
  MessageBar,
  MessageBarType,
  SearchBox,
  Selection,
  TextField,
  ComboBox,
  ISelectableOption,
  IComboBox,
} from '@fluentui/react';

import { AsyncData } from '../../redux/core';
import { FeeSchedule } from '../../redux/fee-schedule/types';
import { HealAPI } from '../../healApi';
import { KeyCodes } from '../../util/Keyboard';
import { PageHeader } from '../core/PageHeader';
import { ClaimCptCodeLookup } from '../claim/ClaimCptCodeLookup';

export interface OwnProps {
  cptSearchString?: string;
}

export interface StateProps {
  feeScheduleList: AsyncData<FeeSchedule[]>;
  selectedPayerId: string;
  selectedBillingProviderId: number;
}

export interface DispatchProps {
  actions: {
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
    setPayerId: (id: string) => void;
    setBillingProviderId: (id: number) => void;
    setCptSearch: (query: string) => void;
    saveFeeSchedule: (feeSchedule: FeeSchedule, isNew: boolean) => void;
  };
}

type FeeScheduleProps = OwnProps & StateProps & DispatchProps;

interface EditableListItem extends IObjectWithKey {
  key: string;
  data: FeeSchedule;
  editableCopy?: {
    cpt?: string;
    groupId?: string;
    allowance?: string;
    effectiveDate?: Date;
    billExactPrice?: boolean;
  };
  isNew?: boolean;
}

export interface FeeScheduleState {
  payerList: Payer[];
  billingProviderList: BillingProvider[];
  listItems: EditableListItem[];
}

export class FeeScheduleComponent extends React.Component<FeeScheduleProps, FeeScheduleState> {
  private selection: Selection;
  private _columns: IColumn[] = [
    {
      key: 'cpt',
      name: 'CPT',
      fieldName: 'cpt',
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
      onRender: (item?: EditableListItem) => {
        if (item && item.editableCopy) {
          return (
            <TextField
              autoFocus={true}
              onChange={(a, value) =>
                this.modifyEditableItem(item, (changableItem: EditableListItem) => {
                  changableItem.editableCopy!.cpt = value;
                })
              }
              onKeyDown={(e) => {
                if (e.keyCode === KeyCodes.enter) {
                  this.acceptFeeScheduleEdits(item);
                }
              }}
              value={item.editableCopy.cpt}
            />
          );
        } else {
          return <span>{!!item && item.data.cpt}</span>;
        }
      },
    },
    {
      key: 'name',
      name: 'Name',
      minWidth: 200,
      maxWidth: 400,
      isResizable: true,
      onRender: (item?: EditableListItem) => {
        return <span>{(!!item && ClaimCptCodeLookup.cptCodes[item.data.cpt]) || ''}</span>;
      },
    },
    {
      key: 'groupId',
      name: 'Group',
      minWidth: 200,
      maxWidth: 400,
      isResizable: true,
      onRender: (item?: EditableListItem) => {
        if (item && item.editableCopy) {
          return (
            <TextField
              autoFocus={true}
              onChange={(a, value) =>
                this.modifyEditableItem(item, (changableItem: EditableListItem) => {
                  changableItem.editableCopy!.groupId = value;
                })
              }
              onKeyDown={(e) => {
                if (e.keyCode === KeyCodes.enter) {
                  this.acceptFeeScheduleEdits(item);
                }
              }}
              value={item.editableCopy.groupId}
            />
          );
        } else {
          return <span>{!!item && item.data.groupId}</span>;
        }
      },
    },
    {
      key: 'allowance',
      name: 'Allowance',
      fieldName: 'allowance',
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
      onRender: (item?: EditableListItem) => {
        if (item && item.editableCopy) {
          return (
            <TextField
              onChange={(a, value) =>
                this.modifyEditableItem(item, (changableItem: EditableListItem) => {
                  changableItem.editableCopy!.allowance = value;
                })
              }
              value={String(item.editableCopy.allowance)}
              onKeyDown={(e) => {
                if (e.keyCode === KeyCodes.enter) {
                  this.acceptFeeScheduleEdits(item);
                }
              }}
            />
          );
        } else {
          return <span>{!!item && item.data.allowance}</span>;
        }
      },
    },
    {
      key: 'effectiveDate',
      name: 'Effective Date',
      fieldName: 'effectiveDate',
      minWidth: 140,
      maxWidth: 140,
      onRender: (item?: EditableListItem) => {
        if (item && item.editableCopy) {
          return (
            <DatePicker
              placeholder="Choose a date"
              formatDate={(d) => moment(d).format('L')}
              value={moment(item.editableCopy.effectiveDate).toDate()}
              onSelectDate={(date) =>
                this.modifyEditableItem(item, (changableItem: EditableListItem) => {
                  changableItem.editableCopy!.effectiveDate = date || undefined;
                })
              }
            />
          );
        } else {
          return <span>{!!item && moment(item.data.effectiveDate).format('L')}</span>;
        }
      },
    },
    {
      key: 'billExact',
      name: 'Bill Exact Amount',
      fieldName: 'billExact',
      minWidth: 100,
      maxWidth: 140,
      onRender: (item?: EditableListItem) => {
        if (item && item.editableCopy) {
          return (
            <Checkbox
              checked={item.editableCopy.billExactPrice}
              onChange={(a, value) =>
                this.modifyEditableItem(item, (changableItem: EditableListItem) => {
                  changableItem.editableCopy!.billExactPrice = value;
                })
              }
            />
          );
        } else {
          return <Checkbox disabled={true} checked={item && item.data.billExactPrice} />;
        }
      },
    },
    {
      key: 'controls',
      name: '',
      fieldName: 'controls',
      minWidth: 100,
      maxWidth: 200,
      isResizable: false,
      onRender: (item?: EditableListItem) => {
        if (item && item.editableCopy) {
          const scopedItem = item;
          return (
            <div>
              <IconButton onClick={() => this.acceptFeeScheduleEdits(scopedItem)} iconProps={{ iconName: 'Accept' }} />
              <IconButton onClick={() => this.discardFeeScheduleEdits(scopedItem)} iconProps={{ iconName: 'Cancel' }} />
            </div>
          );
        } else {
          return <span />;
        }
      },
    },
  ];

  constructor(props: FeeScheduleProps) {
    super(props);
    this.state = {
      payerList: [],
      billingProviderList: [],
      listItems: [],
    };
    this.selection = new Selection();
  }

  public componentDidMount() {
    this.props.actions.onPageDidMount('FeeSchedule');

    HealAPI.fetchPayers().then((response) => {
      if (response.ok && response.data) {
        // Entry of fee schedules is not needed for Medicare; (they get calculated server-side from CMS sources)
        const payers = response.data.filter((payer) => payer.name !== 'Medicare');
        this.setState({ payerList: payers });
        this.props.actions.setPayerId(payers[0].id);
      }
    });

    HealAPI.fetchBillingProviders().then((response) => {
      if (response.ok && response.data) {
        this.setState({ billingProviderList: response.data });
        this.props.actions.setBillingProviderId(response.data[0].id);
      }
    });
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('FeeSchedule');
  }

  public componentDidUpdate(prevProps: FeeScheduleProps) {
    if (prevProps.feeScheduleList.data !== this.props.feeScheduleList.data)
      this.setState({
        ...this.state,
        listItems: (this.props.feeScheduleList.data || []).map((fs: FeeSchedule) => {
          return {
            key: fs.id,
            data: fs,
          };
        }),
      });
  }

  public render(): JSX.Element {
    return (
      <div>
        <PageHeader title="Fee Schedule" isLoading={this.props.feeScheduleList.isFetching} />
        <div className="flex">
          <div style={{ marginRight: 10 }}>{this.renderPayer()}</div>
          <div style={{ marginRight: 10 }}>{this.renderBillingProvider()}</div>
          <div className="flex-self-end" style={{ marginRight: 10 }}>
            <SearchBox placeholder="Search CPT" onChange={this.onCptChange} />
          </div>
          <CommandBarButton className="flex-self-end" style={{ marginRight: 10, height: 32 }} iconProps={{ iconName: 'Add' }} onClick={this.onAddClicked} text="Add" />
          {/* <CommandBarButton
            className="flex-self-end"
            style={{ marginRight: 10, height: 32 }}
            iconProps={{ iconName: 'Delete' }}
            onClick={() => alert('This feature is not yet implemented.')}
            text="Delete"
          /> */}
          <CommandBarButton className="flex-self-end" style={{ marginRight: 10, height: 32 }} iconProps={{ iconName: 'Edit' }} onClick={this.onEditClicked} text="Edit" />
        </div>
        <DetailsList items={this.state.listItems} columns={this._columns} selection={this.selection} onItemInvoked={this.onItemInvoked} />
        {(this.props.feeScheduleList.data || []).length === 0 && !this.props.feeScheduleList.isFetching && (
          <MessageBar messageBarType={MessageBarType.info} isMultiline={false}>
            No Results
          </MessageBar>
        )}
      </div>
    );
  }

  private discardFeeScheduleEdits = (item: EditableListItem) => {
    this.disableEditing(item.key);
  };

  private acceptFeeScheduleEdits = (item: EditableListItem) => {
    item.data.cpt = item.editableCopy!.cpt || '';
    item.data.allowance = Number(item.editableCopy!.allowance) || 0;
    item.data.effectiveDate = item.editableCopy!.effectiveDate!.toISOString();
    item.data.billExactPrice = item.editableCopy!.billExactPrice || false;
    item.data.groupId = item.editableCopy!.groupId || '';
    this.props.actions.saveFeeSchedule(item.data, item.isNew || false);
  };

  private renderPayer(): JSX.Element {
    const payerNameList: ISelectableOption[] = (this.state.payerList || [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((payer) => {
        return {
          text: payer.name,
          key: payer.id,
        };
      });

    return (
      <ComboBox label="Select Payer" selectedKey={this.props.selectedPayerId} allowFreeform={false} autoComplete="on" options={payerNameList} onChange={this.onPayerChanged} />
    );
  }

  private renderBillingProvider(): JSX.Element {
    const billingProviderList: IDropdownOption[] = (this.state.billingProviderList || []).map((bp) => {
      return {
        text: `${bp.description}`,
        key: bp.id,
      };
    });

    return (
      <Dropdown
        style={{ width: 350 }}
        label="Billing Provider"
        selectedKey={this.props.selectedBillingProviderId}
        options={billingProviderList}
        onChange={this.onBillingProviderChanged}
      />
    );
  }

  private onAddClicked = () => {
    const newItem: EditableListItem | undefined = this.generateNewListItem();

    if (newItem) {
      const newArray = this.state.listItems.slice();
      newArray.unshift(newItem);
      this.setState({ listItems: newArray }, () => this.enableEditing(newItem!));
    }
  };

  private onEditClicked = () => {
    const feeSchedulesToEdit = this.selection.getSelection();
    if (feeSchedulesToEdit.length > 0) {
      this.enableEditing(feeSchedulesToEdit[0] as EditableListItem);
    }
  };

  private generateNewListItem(): EditableListItem | undefined {
    const payer: Payer | undefined = this.state.payerList.find((bp) => bp.id === this.props.selectedPayerId);
    const billingProvider: BillingProvider | undefined = this.state.billingProviderList.find((bp) => bp.id === this.props.selectedBillingProviderId);

    if (!payer || !billingProvider) {
      return undefined;
    }

    return {
      key: '',
      data: {
        id: '',
        cpt: '',
        payer: payer,
        billingProvider: billingProvider,
        allowance: 159,
        placeOfService: 'Home',
        effectiveDate: new Date().toISOString(),
        billExactPrice: false,
      },
      isNew: true,
    };
  }

  private modifyEditableItem = (item: EditableListItem, modify: (item: EditableListItem) => void) => {
    const modifiedItem = { ...item };
    modify(modifiedItem);

    this.setState({
      listItems: this.state.listItems.map((li) => (item === li ? modifiedItem : li)),
    });
  };

  private enableEditing(item: EditableListItem) {
    const newListItems = this.state.listItems
      .map((li: EditableListItem) => {
        if (li === item) {
          li.editableCopy = {
            cpt: li.data.cpt,
            groupId: li.data.groupId,
            allowance: String(li.data.allowance),
            billExactPrice: li.data.billExactPrice,
            effectiveDate: moment(li.data.effectiveDate).toDate(),
          };
        } else {
          // turn off editing on other item, only one at a time
          li.editableCopy = undefined;
        }
        return li;
      })
      .filter((li: EditableListItem) => {
        return !li.isNew || !!li.editableCopy; // we just enabled editing, are there other items that are new that aren't editing?
      });
    this.setState({ listItems: newListItems });
  }

  private disableEditing(itemKey: string) {
    const newListItems = this.state.listItems
      .map((li: EditableListItem) => {
        if (li.key === itemKey) {
          li.editableCopy = undefined;
        }
        return li;
      })
      .filter((li: EditableListItem) => {
        return !li.isNew;
      });
    this.setState({ listItems: newListItems });
  }

  private onItemInvoked = (item: EditableListItem) => {
    this.enableEditing(item);
  };

  private onCptChange = (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
    this.props.actions.setCptSearch(newValue!);
  };

  private onPayerChanged = (event: React.FormEvent<IComboBox>, item?: ISelectableOption) => {
    if (item) {
      this.props.actions.setPayerId(item.key as string);
    }
  };

  private onBillingProviderChanged = (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption) => {
    if (item) {
      this.props.actions.setBillingProviderId(item.key as number);
    }
  };
}
